import React, { useState } from 'react';
import { 
  Book, 
  ChevronDown, 
  ChevronRight,
  LayoutDashboard,
  ClipboardList,
  FileText,
  Calendar,
  Users,
  Wrench,
  Search,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  CalendarClock,
  DollarSign,
  Filter,
  Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';

const instructions = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: LayoutDashboard,
    color: 'text-blue-600',
    description: 'Panel principal con métricas y resumen del negocio',
    sections: [
      {
        title: '¿Qué muestra?',
        content: [
          'Total de servicios del día, semana y mes',
          'Ingresos totales y promedio por servicio',
          'Clientes nuevos vs recurrentes',
          'Servicios por estado (pendientes, confirmados, finalizados)',
          'Técnicos más activos'
        ]
      },
      {
        title: '¿Cómo usarlo?',
        content: [
          'Revisa diariamente para tener un panorama general',
          'Usa los filtros de fecha para ver períodos específicos',
          'Identifica tendencias en tus métricas'
        ]
      }
    ]
  },
  {
    id: 'servicios',
    title: 'Servicios',
    icon: ClipboardList,
    color: 'text-green-600',
    description: 'Gestión completa de todos los servicios agendados',
    sections: [
      {
        title: 'Crear Nuevo Servicio',
        content: [
          'Clic en "Nuevo Servicio"',
          'Llena los datos del cliente (nombre, teléfono, dirección)',
          'Selecciona tipo de servicio y describe el trabajo',
          'Asigna fecha, hora y técnico',
          'Ingresa el costo cotizado',
          'El sistema asigna un folio automático (0001, 0002...)'
        ]
      },
      {
        title: 'Finalizar Servicio',
        content: [
          'Abre el menú del servicio (tres puntos)',
          'Selecciona "Finalizar Servicio"',
          'Ingresa el Folio de la Nota (número de factura)',
          'Ingresa el Monto Cobrado real',
          'Opcionalmente agrega comentarios de cierre',
          'Puedes marcar "Enviar a Presupuestos" si el cliente necesita seguimiento'
        ]
      },
      {
        title: 'Reagendar Servicio',
        content: [
          'Útil cuando el cliente tiene anticipo y necesita cambiar fecha',
          'Abre el menú y selecciona "Reagendar"',
          'Selecciona la nueva fecha y hora',
          'Si el cliente dio un nuevo abono, ingrésalo',
          'El sistema suma automáticamente al anticipo anterior',
          'Escribe el motivo del reagendado'
        ]
      },
      {
        title: 'Estados de Servicio',
        content: [
          'Pendiente: Servicio agendado, esperando confirmación',
          'Confirmado: Cliente confirmó la cita',
          'En Proceso: Técnico está realizando el trabajo',
          'Finalizado: Trabajo completado y cobrado',
          'Reagendado: Se cambió la fecha del servicio',
          'Cancelado: Servicio cancelado'
        ]
      },
      {
        title: 'Filtros y Búsqueda',
        content: [
          'Busca por folio, cliente, teléfono o dirección',
          'Filtra por estado (Pendientes, Confirmados, etc.)',
          'Filtra por fecha (Hoy, Mañana, Esta Semana, Este Mes)',
          'Usa "Limpiar" para quitar todos los filtros'
        ]
      }
    ]
  },
  {
    id: 'presupuestos',
    title: 'Presupuestos',
    icon: FileText,
    color: 'text-purple-600',
    description: 'Cotizaciones y presupuestos antes de agendar',
    sections: [
      {
        title: 'Crear Presupuesto',
        content: [
          'Clic en "Nuevo Presupuesto"',
          'Ingresa datos del cliente',
          'Describe el servicio y el monto estimado',
          'Asigna un técnico sugerido (opcional)',
          'El folio se genera automático (P0001, P0002...)'
        ]
      },
      {
        title: 'Convertir a Servicio (Agendar)',
        content: [
          'Cuando el cliente acepta, abre el menú del presupuesto',
          'Selecciona "Agendar Servicio"',
          'Ingresa fecha y hora del servicio',
          'Selecciona el técnico que irá',
          'Ingresa el Monto Total acordado',
          'Si el cliente dio anticipo, ingrésalo',
          'El sistema calcula automáticamente el Restante',
          'Agrega comentarios importantes',
          'El presupuesto cambia a estado "Agendado"'
        ]
      },
      {
        title: 'Estados de Presupuesto',
        content: [
          'Pendiente: Esperando respuesta del cliente',
          'En Revisión: Cliente está evaluando',
          'Enviado: Se envió cotización al cliente',
          'Aprobado: Cliente aceptó pero no se ha agendado',
          'Agendado: Convertido a servicio',
          'Rechazado: Cliente no aceptó',
          'Sin Respuesta: No hubo respuesta del cliente'
        ]
      },
      {
        title: 'Filtrar por Técnico',
        content: [
          'Abre los filtros',
          'Selecciona un técnico específico',
          'Verás solo los presupuestos asignados a ese técnico'
        ]
      }
    ]
  },
  {
    id: 'calendario',
    title: 'Calendario',
    icon: Calendar,
    color: 'text-orange-600',
    description: 'Vista mensual de todos los servicios agendados',
    sections: [
      {
        title: 'Navegación',
        content: [
          'Usa las flechas para cambiar de mes',
          'Los días con servicios muestran indicadores de color',
          'Haz clic en un día para ver los servicios de esa fecha'
        ]
      },
      {
        title: 'Filtrar por Técnico',
        content: [
          'Usa el dropdown "Todos los técnicos"',
          'Selecciona un técnico específico',
          'El calendario mostrará solo sus servicios'
        ]
      },
      {
        title: 'Detalle del Día',
        content: [
          'Al hacer clic en un día, se abre el detalle',
          'Muestra: hora, cliente, teléfonos, dirección',
          'Muestra: descripción del trabajo, comentarios',
          'Muestra: información de pago (total, anticipo, restante)',
          'Muestra: técnico asignado'
        ]
      },
      {
        title: 'Acciones desde el Calendario',
        content: [
          'Finalizar: Marca el servicio como completado',
          'A Presupuesto: Envía a módulo de presupuestos',
          'Reagendar: Cambia fecha y agrega abonos si aplica'
        ]
      }
    ]
  },
  {
    id: 'clientes',
    title: 'Clientes',
    icon: Users,
    color: 'text-cyan-600',
    description: 'Cartera de clientes con historial completo',
    sections: [
      {
        title: 'Agregar Cliente',
        content: [
          'Clic en "Nuevo Cliente"',
          'Ingresa nombre completo',
          'Agrega teléfonos (puedes agregar varios)',
          'Agrega direcciones con referencias',
          'Los clientes también se crean automáticamente al agendar servicios'
        ]
      },
      {
        title: 'Ficha del Cliente',
        content: [
          'Haz clic en cualquier cliente para ver su ficha',
          'Muestra: Total de atenciones, Total cobrado, Total cotizado',
          'Muestra: Último técnico que lo atendió',
          'Muestra: Todos los técnicos que han ido con ese cliente',
          'Muestra: Historial completo de servicios y presupuestos'
        ]
      },
      {
        title: 'Historial del Cliente',
        content: [
          'Fecha de cada movimiento',
          'Tipo: Servicio o Presupuesto',
          'Folio y folio de nota',
          'Estado del servicio',
          'Monto total, anticipo y restante',
          'Técnico que atendió',
          'Comentarios'
        ]
      },
      {
        title: 'Buscar Clientes',
        content: [
          'Busca por nombre, teléfono o dirección',
          'También puedes buscar por folio de nota',
          'Ejemplo: buscar "1236" encuentra al cliente con ese folio'
        ]
      },
      {
        title: 'Eliminar Cliente',
        content: [
          'Al eliminar un cliente, se eliminan también:',
          '- Todos sus servicios',
          '- Todos sus presupuestos',
          'Esta acción no se puede deshacer'
        ]
      }
    ]
  },
  {
    id: 'tecnicos',
    title: 'Técnicos',
    icon: Wrench,
    color: 'text-red-600',
    description: 'Gestión del equipo de técnicos',
    sections: [
      {
        title: 'Agregar Técnico',
        content: [
          'Clic en "Nuevo Técnico"',
          'Ingresa nombre completo',
          'Teléfono (opcional)',
          'Especialidad (opcional)'
        ]
      },
      {
        title: 'Activar/Desactivar',
        content: [
          'Los técnicos inactivos no aparecen en los dropdowns',
          'Pero su historial de servicios se conserva',
          'Útil para técnicos que ya no trabajan contigo'
        ]
      },
      {
        title: 'Métricas',
        content: [
          'Cada técnico muestra cuántos servicios ha realizado',
          'Puedes ver qué técnicos son los más activos'
        ]
      }
    ]
  },
  {
    id: 'pagos',
    title: 'Sistema de Pagos',
    icon: DollarSign,
    color: 'text-emerald-600',
    description: 'Manejo de anticipos, abonos y pagos',
    sections: [
      {
        title: 'Al Agendar desde Presupuesto',
        content: [
          'Monto Total: Precio acordado del servicio',
          'Anticipo: Cantidad que el cliente pagó por adelantado',
          'Restante: Se calcula automático (Total - Anticipo)',
          'El anticipo no puede ser mayor al total'
        ]
      },
      {
        title: 'Al Reagendar',
        content: [
          'Si el cliente da un nuevo abono al reagendar:',
          'El sistema suma el nuevo abono al anticipo anterior',
          'Ejemplo: Anticipo $1,000 + Nuevo abono $500 = Total $1,500',
          'El restante se actualiza automáticamente'
        ]
      },
      {
        title: 'Al Finalizar',
        content: [
          'Folio de Nota: El número de tu factura o recibo',
          'Monto Cobrado: Lo que realmente cobraste',
          'Esta información se guarda en el historial del cliente'
        ]
      },
      {
        title: 'Colores de Pago',
        content: [
          'Verde: Anticipo o monto pagado',
          'Naranja: Restante por pagar',
          'Los colores te ayudan a identificar rápidamente el estado de pago'
        ]
      }
    ]
  }
];

export default function Instructions() {
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);

  const toggleSection = (moduleId, sectionIndex) => {
    const key = `${moduleId}-${sectionIndex}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSectionExpanded = (moduleId, sectionIndex) => {
    return expandedSections[`${moduleId}-${sectionIndex}`] ?? true;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Book className="h-8 w-8 text-primary" />
            Instrucciones del Sistema
          </h1>
          <p className="text-muted-foreground mt-1">
            Guía completa de todas las funciones de REPACS SISTEMA
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar de módulos */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Módulos</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {instructions.map((module) => {
                  const Icon = module.icon;
                  return (
                    <button
                      key={module.id}
                      onClick={() => setSelectedModule(module.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        selectedModule === module.id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted'
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${selectedModule === module.id ? '' : module.color}`} />
                      <span className="text-sm font-medium">{module.title}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contenido principal */}
        <div className="lg:col-span-3">
          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="space-y-6 pr-4">
              {instructions
                .filter(m => !selectedModule || m.id === selectedModule)
                .map((module) => {
                  const Icon = module.icon;
                  return (
                    <Card key={module.id} id={module.id}>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-muted ${module.color}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">{module.title}</h2>
                            <p className="text-sm text-muted-foreground font-normal">
                              {module.description}
                            </p>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {module.sections.map((section, sectionIndex) => (
                          <div key={sectionIndex} className="border rounded-lg">
                            <button
                              onClick={() => toggleSection(module.id, sectionIndex)}
                              className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                            >
                              <span className="font-medium text-left">{section.title}</span>
                              {isSectionExpanded(module.id, sectionIndex) ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                            </button>
                            {isSectionExpanded(module.id, sectionIndex) && (
                              <div className="px-4 pb-4">
                                <ul className="space-y-2">
                                  {section.content.map((item, itemIndex) => (
                                    <li key={itemIndex} className="flex items-start gap-2 text-sm">
                                      <span className="text-primary mt-1">•</span>
                                      <span className="text-muted-foreground">{item}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
